import { Chip, type ChipOwnProps } from "@mui/material";
import type { ReactElement } from "react";
import { humanizeText } from "../../utils/text";

/**
 * Configuration for a single status value in a StatusChip.
 * Each status maps to a color, optional icon, and optional per-status variant override.
 */
export type StatusEntry = {
  icon?: ReactElement;
  color: ChipOwnProps["color"];
  variant?: ChipOwnProps["variant"];
};

export type StatusChipProps<T extends string> = {
  status: T;
  statusMap: Partial<Record<T, StatusEntry>>;
  formatLabel?: (status: string) => string;
  variant?: ChipOwnProps["variant"];
  size?: ChipOwnProps["size"];
  label?: string;
  sx?: ChipOwnProps["sx"];
};

const DEFAULT_ENTRY: StatusEntry = { color: "default" };

/**
 * Generic status chip component. Accepts a status map that defines icon, color,
 * and optional variant override for each status value.
 *
 * Domain-specific wrappers (RunStatusChip, SessionStatusChip, etc.) pre-configure
 * the statusMap and defaults, exposing a simple `status` prop to consumers.
 */
export function StatusChip<T extends string>({
  status,
  statusMap,
  formatLabel = humanizeText,
  variant = "outlined",
  size = "small",
  label,
  sx,
}: StatusChipProps<T>) {
  const entry = statusMap[status] ?? DEFAULT_ENTRY;
  return (
    <Chip
      label={label ?? formatLabel(status)}
      variant={entry.variant ?? variant}
      icon={entry.icon}
      color={entry.color}
      size={size}
      sx={sx}
    />
  );
}
